/*
 * @Author: chenhe ch10@meitu.com
 * @Date: 2025-10-09 10:21:42
 * @LastEditors: chenhe ch10@meitu.com
 * @LastEditTime: 2025-10-22 18:33:28
 * @FilePath: /roboneo-web/packages/roboneo-sdk/src/pages/RoboneoContext/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import React, {
  useEffect, lazy, Suspense, useState,
} from 'react';
import type { MediaPreviewProps } from '$/components/MediaPreview';
import UploadErrorToast from '$/components/UploadErrorToast';
import UploadLoadingProgress from '$/components/UploadLoadingProgress';
import usePreviewStore from '$/store/usePreviewStore';
import { MyIpMediaPreviewHostContext } from '$/components/MyIpMediaPreview/hostContext';
import { MessageProvider, message } from '$/components/Message';
import { useOfficialStore } from '$/store/useOfficialStore';
import useVipModalStore, { openVipModal, type OpenMode, type VipMembershipMode } from '$/store/useVipModalStore';
import { getSubscribe } from '$/common/utils/subscribeUtil';
// import VipModal from '$/components/VipModal';
import { useDocumentVisibility } from 'ahooks';
import { getInitConfigList } from '$/api/server';
import { getUrlSearchParams } from '$/utils';
import { PreviewMediaItem } from '../RoboneoChatBoard/type';

export interface RoboneoContextProps {
  children: React.ReactNode;
  enableVip?: boolean;
  MediaPreviewComponent?: React.ComponentType<MediaPreviewProps>;
  onDownloaded?: (media: PreviewMediaItem) => void | Promise<void>;
  vipLevelNumber?: number | 'all';
  disableRadishConfig?: boolean; // 禁用萝卜赠送请求
  onCreateQrcodeBefore?: (options: { membershipMode: VipMembershipMode; }) => Promise<boolean>;
  /** 是否关闭公共预览下载按钮的会员无品牌水印权益引导，下载能力本身不受影响 */
  disablePreviewDownloadPrivilegeGuide?: boolean;
}

const VipModal = lazy(() => import('$/components/VipModal'));
const SingleProductPayModal = lazy(() => import('$/components/VipModal/SingleProductPayModal'));
// MediaPreview / VideoComModal 依赖 swiper / xgplayer / react-zoom-pan-pinch，仅预览/播放时才需要。
// 改为 lazy 加载，避免这些包随 RoboneoLayout 进入全站首屏（被提升到 pages/_app）。
const MediaPreview = lazy(() => import('$/components/MediaPreview'));
const VideoComModal = lazy(() => import('$/components/VideoComModal'));

const OPEN_VIP_SESSION_KEY = 'openVip';

/** 消费订阅入口参数后保留路由状态和其他查询参数，避免关闭弹窗后被重复拉起。 */
function stripModalPurchaseFromUrl() {
  const url = new URL(window.location.href);
  if (url.searchParams.get('modal') !== 'purchase') return;
  url.searchParams.delete('modal');
  const qs = url.searchParams.toString();
  window.history.replaceState(window.history.state, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`);
}

/**
 * 挂载 RoboNeo 全局上下文能力，包括全局弹窗、toast、预览和上传反馈。
 */
function RoboneoContext({
  children,
  enableVip = true, // 默认开启
  MediaPreviewComponent,
  onDownloaded,
  vipLevelNumber = 'all',
  disableRadishConfig = false,
  onCreateQrcodeBefore,
  disablePreviewDownloadPrivilegeGuide = false,
}: RoboneoContextProps) {
  const isShowVideo = usePreviewStore((state) => state.isShowVideo);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const isInitComponent = useOfficialStore((state) => state.isInitComponent);
  const shouldOpenSingleProductModal = useVipModalStore((state) => state.open && state.modalType === 'single');
  const [shouldLoadVip, setShouldLoadVip] = useState(false);
  const documentVisibility = useDocumentVisibility();
  useEffect(() => {
    if (isUserLoggedIn && isInitComponent && shouldLoadVip) {
      getSubscribe();
    }
  }, [isUserLoggedIn, isInitComponent, shouldLoadVip]);
  useEffect(() => {
    if (isUserLoggedIn && documentVisibility === 'visible' && isInitComponent && !disableRadishConfig) {
      getInitConfigList()
        .then((res) => {
          if (res.scene) {
            message.info(`${res.title}${res.send_num}`);
          }
        })
        .catch(() => {});
    }
  }, [disableRadishConfig, documentVisibility, isUserLoggedIn, isInitComponent]);
  // 条件加载逻辑：订阅弹窗仍登录后挂载；全局单购弹窗打开时也触发懒加载。
  useEffect(() => {
    if (!enableVip) return;
    if (isUserLoggedIn || shouldOpenSingleProductModal) {
      setShouldLoadVip(true);
    }
  }, [isUserLoggedIn, enableVip, shouldOpenSingleProductModal]);

  // 统一复用 modal=purchase 入口，跳转落地并初始化后才打开订阅。
  useEffect(() => {
    if (!enableVip || typeof window === 'undefined' || !isInitComponent) return;
    if (getUrlSearchParams().get('modal') !== 'purchase') return;

    if (isUserLoggedIn) {
      sessionStorage.removeItem(OPEN_VIP_SESSION_KEY);
      stripModalPurchaseFromUrl();
      openVipModal('url_purchase');
      return;
    }

    sessionStorage.setItem(OPEN_VIP_SESSION_KEY, 'url_purchase');
    stripModalPurchaseFromUrl();
    useOfficialStore.getState().toLogin().catch(() => {});
  }, [enableVip, isInitComponent, isUserLoggedIn]);

  // 登录成功后打开充值弹窗
  useEffect(() => {
    if (!enableVip || !isUserLoggedIn || !isInitComponent) return;
    const openVipSource = sessionStorage.getItem(OPEN_VIP_SESSION_KEY);
    if (!openVipSource) return;
    sessionStorage.removeItem(OPEN_VIP_SESSION_KEY);
    openVipModal(openVipSource as OpenMode);
  }, [enableVip, isUserLoggedIn, isInitComponent]);
  // 加载中的回退组件
  // eslint-disable-next-line react/no-unstable-nested-components
  function LoadingFallback() {
    return <div />;
  } // 隐藏的加载状态

  return (
    <>
      {/* Toast */}
      <MessageProvider />
      <UploadLoadingProgress />
      <UploadErrorToast />
      {shouldLoadVip
      && (
      <Suspense fallback={LoadingFallback()}>
        <VipModal
          enableVip={enableVip}
          vipLevelNumber={vipLevelNumber}
          onCreateQrcodeBefore={onCreateQrcodeBefore}
        />
        <SingleProductPayModal enableVip={enableVip} vipLevelNumber={vipLevelNumber} />
      </Suspense>
      )}

      {/* 订阅 */}
      {/* <VipModal /> */}

      {/* 图片预览 */}
      <Suspense fallback={LoadingFallback()}>
        <MediaPreview
          MediaPreviewComponent={MediaPreviewComponent}
          onDownloaded={onDownloaded}
          disablePreviewDownloadPrivilegeGuide={disablePreviewDownloadPrivilegeGuide}
        />
      </Suspense>

      {/* 视频播放 */}
      {isShowVideo && (
        <Suspense fallback={LoadingFallback()}>
          <VideoComModal />
        </Suspense>
      )}

      <MyIpMediaPreviewHostContext.Provider value>{children}</MyIpMediaPreviewHostContext.Provider>
    </>
  );
}

export default RoboneoContext;
